import ScreenHeaderButton from 'common/components/screenHeader/ScreenHeaderButton';
import ScreenTitle from 'common/components/screenTitle/ScreenTitle'
import AddIcon from 'assets/svg/desktop_add_new.svg';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styles from './AlarmsListScreen.module.scss';
import { Filters } from '../alarmsFilters/AlarmsFilters';
import { DATE_TIME_FORMAT_DEFAUT, DEFAULT_PAGINATION_ITEMS_PER_PAGE, MAX_PAGINATION_ITEMS_PER_PAGE, LOGGER_LOG_TYPE } from 'Config';
import { AlarmsSearchCriteria } from 'api/alarms/models/AlarmsSearchCriteria';
import { AlarmsListItemDto } from 'api/alarms/models/AlarmsListItemDto';
import DateFormat from 'common/components/dateFormat/dateFormat';
import moment from 'moment';
import toggleSwitchStyle from 'common/components/toggleSwitch/ToggleSwitch.module.scss'
import InputSearch from 'common/components/inputSearch/InputSearch';
import { useDebouncedCallback } from 'use-debounce';
import Loading from 'common/services/Loading';
import AlarmsService from 'api/alarms/AlarmsService';
import Logger from 'common/services/Logger';
import { useToasts } from 'react-toast-notifications';
import Dropdown from 'common/components/dropdown/Dropdown';
import DropdownItem from 'common/components/dropdown/DropdownItem';
import { FaEllipsisH } from 'react-icons/fa';
import ExportListingButton from 'common/components/exportListingButton/ExportListingButton';
import { AlarmSettingDto } from 'api/alarms/models/AlarmSettingDto';
import QuestionYesNo from 'common/components/questionYesNo/QuestionYesNo';
import { CompactPageLayout, compactPageLayoutStyles } from 'common/components/compactPageLayout';
import DataTableWithSidebar from 'common/components/dataTable/DataTableWithSidebar';
import { DataTableColumn, OrderData } from 'common/components/dataTable/models';
import useBidirectionalScroll from 'common/hooks/useBidirectionalScroll';

const FILTERS_CACHE_KEY = 'ALARMSFILTER';

interface AlarmDropdownProps {
    row: AlarmsListItemDto;
    onRemoveClick: (row: AlarmsListItemDto) => void;
}

const AlarmDropdown: React.FC<AlarmDropdownProps> = ({ row, onRemoveClick }) => {
    const { t } = useTranslation();

    return (
        <Dropdown
            options={
                <>
                    <DropdownItem url={`/alarms/details/${row.id}`}>
                        {t('common.details')}
                    </DropdownItem>
                    <DropdownItem
                        url={`/alarms/edit/${row.id}`}
                        disabled={!row.canEdit}
                        disabled_message={t('alarms.no_permission_edit_tooltip')}
                    >
                        {t('common.edit')}
                    </DropdownItem>
                    <DropdownItem
                        onClick={() => onRemoveClick(row)}
                        disabled={!row.canEdit}
                        disabled_message={t('alarms.no_permission_remove_tooltip')}
                    >
                        {t('common.remove')}
                    </DropdownItem>
                </>
            }
        >
            <div>
                <FaEllipsisH />
            </div>
        </Dropdown>
    );
};

const AlarmsListScreen = () => {
    const { t } = useTranslation();
    const history = useHistory();
    const { addToast } = useToasts();

    const [alarms, setAlarms] = useState<AlarmsListItemDto[]>([]);
    const [totalItems, setTotalItems] = useState<number>(0);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [criteria, setCriteria] = useState<AlarmsSearchCriteria>({
        itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
        page: 1,
        orderColumn: 'st.name',
        orderBy: 'asc'
    });

    const [dialogDeleteItemIsOpen, setDialogDeleteItemIsOpen] = useState(false);
    const [alarmToRemove, setAlarmToRemove] = useState<AlarmsListItemDto | null>(null);

    const isFirstLoad = useRef(true);
    const isLoadingData = useRef(false);
    const exportListingRef = useRef<{ exportExcel: () => Promise<void> } | null>(null);

    // Bidirectional scroll hook
    const bidirectionalScroll = useBidirectionalScroll({
        cacheKey: FILTERS_CACHE_KEY,
        itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
    });

    const {
        loadedPageRange,
        isLoadingPrevious,
        scrollToRowId: scrollToItemId,
        shouldLoadAdjacentPages,
        setLoadedPageRange,
        setIsLoadingPrevious,
        resetScrollState,
        calculatePageFromIndex,
        prepareLoadPrevious,
        adjustScrollAfterPrepend,
        finishRestoration,
        initiateRestoration,
        getHasMore,
        getHasMorePrevious,
        getOnLoadMore,
        getOnLoadPrevious,
    } = bidirectionalScroll;

    // Order state for DataTable
    const [order, setOrder] = useState<OrderData<AlarmsListItemDto>>({
        field: 'name',
        orderColumn: 'st.name',
        isOrderAsc: true,
    });

    const sortByTypeClientSide = (items: AlarmsListItemDto[], orderBy?: string) => {
        items.sort((a, b) => {
            const valA = t('alarms.types.' + a.type as any);
            const valB = t('alarms.types.' + b.type as any);
            const comparison = valA.localeCompare(valB);
            return orderBy === 'desc' ? -comparison : comparison;
        });
    };

    const getData = async (isLoadMore = false) => {
        if (isLoadingData.current) return;
        isLoadingData.current = true;

        try {
            if (!isLoadMore) {
                Loading.show();
            } else {
                setIsLoadingMore(true);
            }

            saveOrdernationFilters();

            const alarmsItems = await AlarmsService.getList(criteria);

            if (criteria.orderColumn === 'st.type') {
                sortByTypeClientSide(alarmsItems.items, criteria.orderBy);
            }

            setTotalItems(alarmsItems.totalItems);

            if (isLoadMore) {
                setAlarms(prev => [...prev, ...alarmsItems.items]);
                setLoadedPageRange(prev => ({ ...prev, max: criteria.page }));
            } else {
                setAlarms(alarmsItems.items ?? []);
            }

            // Handle scroll restoration
            if (shouldLoadAdjacentPages.current) {
                shouldLoadAdjacentPages.current = false;
                const currentPage = criteria.page;
                const totalPages = Math.ceil(alarmsItems.totalItems / (criteria.itemsPerPage || DEFAULT_PAGINATION_ITEMS_PER_PAGE));

                if (currentPage > 1) {
                    try {
                        const prevCriteria = { ...criteria, page: currentPage - 1 };
                        const prevResult = await AlarmsService.getList(prevCriteria);
                        if (criteria.orderColumn === 'st.type') {
                            sortByTypeClientSide(prevResult.items, criteria.orderBy);
                        }
                        setAlarms(prev => [...prevResult.items, ...prev]);
                        setLoadedPageRange(prev => ({ ...prev, min: currentPage - 1 }));
                    } catch (e) {
                        Logger.error(LOGGER_LOG_TYPE.ALARMS, `Couldn't load previous page`, e);
                    }
                }

                if (currentPage < totalPages) {
                    try {
                        const nextCriteria = { ...criteria, page: currentPage + 1 };
                        const nextResult = await AlarmsService.getList(nextCriteria);
                        if (criteria.orderColumn === 'st.type') {
                            sortByTypeClientSide(nextResult.items, criteria.orderBy);
                        }
                        setAlarms(prev => [...prev, ...nextResult.items]);
                        setLoadedPageRange(prev => ({ ...prev, max: currentPage + 1 }));
                    } catch (e) {
                        Logger.error(LOGGER_LOG_TYPE.ALARMS, `Couldn't load next page`, e);
                    }
                }

                finishRestoration();
            }
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.ALARMS, `Couldn't get alarms list`, error);
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
            resetScrollState();
        } finally {
            Loading.hide();
            setIsLoadingMore(false);
            isLoadingData.current = false;
        }
    };

    // Handler for infinite scroll load more
    const loadMore = useCallback(() => {
        if (isLoadingData.current || isLoadingMore || alarms.length >= totalItems) return;
        const nextPage = loadedPageRange.max + 1;
        setCriteria(prev => ({ ...prev, page: nextPage }));
    }, [isLoadingMore, alarms.length, totalItems, loadedPageRange.max]);

    // Load previous pages (bidirectional scroll)
    const loadPrevious = useCallback(async () => {
        if (isLoadingPrevious || loadedPageRange.min <= 1) return;

        setIsLoadingPrevious(true);
        const prevPage = loadedPageRange.min - 1;

        try {
            prepareLoadPrevious();
            const prevCriteria: AlarmsSearchCriteria = {
                ...criteria,
                page: prevPage,
                itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
            };
            const result = await AlarmsService.getList(prevCriteria);
            if (criteria.orderColumn === 'st.type') {
                sortByTypeClientSide(result.items, criteria.orderBy);
            }

            setAlarms(prev => [...result.items, ...prev]);
            setLoadedPageRange(prev => ({ ...prev, min: prevPage }));
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.ALARMS, `Couldn't get previous alarms page`, error);
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
        } finally {
            setIsLoadingPrevious(false);
        }
    }, [isLoadingPrevious, loadedPageRange.min, criteria, addToast, t]);

    // Adjust scroll position after prepending items
    useEffect(() => {
        if (!isLoadingPrevious) {
            adjustScrollAfterPrepend();
        }
    }, [alarms, isLoadingPrevious, adjustScrollAfterPrepend]);

    // Handler for order change
    const handleOrderChange = (orderData: OrderData<AlarmsListItemDto>) => {
        setOrder(orderData);
        setAlarms([]);
        resetScrollState();
        setTotalItems(0);
        setCriteria(prev => ({
            ...prev,
            page: 1,
            orderBy: orderData.isOrderAsc ? 'asc' : 'desc',
            orderColumn: orderData.orderColumn || orderData.field as string,
        }));
    };

    const navigateTo = (type?: string, id?: string) => {
        if (id) {
            history.push(`/alarms/${type}/${id}`);
        } else {
            history.push(`/alarms/${type}`);
        }
    };

    const onRowClick = (event: any, row: AlarmsListItemDto, index: number) => {
        if (row.id) {
            event.preventDefault();
            event.stopPropagation();

            const actualPage = calculatePageFromIndex(index);

            const newFilters: Filters = {
                allIn: criteria.allIn,
                orderBy: criteria.orderBy,
                orderColumn: criteria.orderColumn,
                page: actualPage,
                scrollToItemId: row.id,
            };
            saveFiltersOnCache(newFilters);

            if (event.ctrlKey) {
                window.open(`/alarms/details/${row.id}`, '_blank');
                return;
            }
            navigateTo('details', row.id);
        }
    };

    const handleRemoveClick = (row: AlarmsListItemDto) => {
        setAlarmToRemove(row);
        setDialogDeleteItemIsOpen(true);
    };

    const renderTableActionCell = (row: AlarmsListItemDto) => {
        return (
            <AlarmDropdown
                row={row}
                onRemoveClick={handleRemoveClick}
            />
        );
    };

    const saveOrdernationFilters = () => {
        saveFiltersOnCache({ allIn: criteria.allIn, orderBy: criteria.orderBy, orderColumn: criteria.orderColumn });
    };

    const saveFiltersOnCache = (f: Filters) => {
        if (f.scrollToItemId && f.page) {
            localStorage.setItem(FILTERS_CACHE_KEY, JSON.stringify(f));
        } else {
            const { page: _page, scrollToItemId: _scrollToItemId, ...filtersWithoutScrollData } = f;
            localStorage.setItem(FILTERS_CACHE_KEY, JSON.stringify(filtersWithoutScrollData));
        }
    };

    useEffect(() => {
        if (isFirstLoad.current === true) {
            loadFilters();
        } else {
            const isLoadMore = criteria.page > loadedPageRange.max && alarms.length > 0;
            getData(isLoadMore);
        }
    }, [criteria]);

    const loadFilters = () => {
        isFirstLoad.current = false;

        const cachedFilters = localStorage.getItem(FILTERS_CACHE_KEY);
        if (cachedFilters != null) {
            const f = JSON.parse(cachedFilters) as Filters;

            if (f.scrollToItemId) {
                const startPage = initiateRestoration({
                    page: f.page || 1,
                    scrollToItemId: f.scrollToItemId,
                });

                const cleanedFilters = { ...f, scrollToItemId: undefined, page: 1 };
                localStorage.setItem(FILTERS_CACHE_KEY, JSON.stringify(cleanedFilters));

                setCriteria(prev => ({
                    ...prev,
                    allIn: f.allIn,
                    orderBy: f.orderBy,
                    orderColumn: f.orderColumn,
                    page: startPage,
                    itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
                }));

                if (f.orderBy && f.orderColumn) {
                    setOrder({
                        field: f.orderColumn as keyof AlarmsListItemDto,
                        orderColumn: f.orderColumn,
                        isOrderAsc: f.orderBy === 'asc',
                    });
                }
                return;
            }

            resetScrollState();
            localStorage.setItem(FILTERS_CACHE_KEY, JSON.stringify({ ...f, scrollToItemId: undefined, page: 1 }));

            setCriteria(prev => ({
                ...prev,
                allIn: f.allIn,
                orderBy: f.orderBy,
                orderColumn: f.orderColumn,
                page: 1,
            }));

            if (f.orderBy && f.orderColumn) {
                setOrder({
                    field: f.orderColumn as keyof AlarmsListItemDto,
                    orderColumn: f.orderColumn,
                    isOrderAsc: f.orderBy === 'asc',
                });
            }
        } else {
            setCriteria(prev => ({ ...prev, page: 1 }));
        }
    };

    const removeItem = async () => {
        setDialogDeleteItemIsOpen(false);
        if (!alarmToRemove) return;

        const alarmAux: AlarmSettingDto = {
            id: alarmToRemove.id,
            name: alarmToRemove.name,
            type: alarmToRemove.type,
            limit: 0,
            rowVersion: alarmToRemove.rowVersion
        };

        try {
            Loading.show();
            await AlarmsService.remove(alarmAux);
            addToast(t('common.messages.record_delete_success'), { appearance: 'success' });
            setAlarms([]);
            resetScrollState();
            setTotalItems(0);
            setCriteria(prev => ({ ...prev, page: 1 }));
        } catch (error) {
            addToast(t('common.messages.record_delete_error'), { appearance: 'error' });
        } finally {
            Loading.hide();
        }
    };

    const debounced = useDebouncedCallback((value: string) => {
        if (value === criteria.allIn) return;
        setAlarms([]);
        resetScrollState();
        setTotalItems(0);
        setCriteria(prev => ({ ...prev, page: 1, allIn: value }));
    }, 500);

    const fetchAllForExport = useCallback(async () => {
        const result = await AlarmsService.getList({
            ...criteria,
            page: 1,
            itemsPerPage: MAX_PAGINATION_ITEMS_PER_PAGE,
        });
        return result.items;
    }, [criteria]);

    const tableColumns: DataTableColumn<AlarmsListItemDto>[] = useMemo(() => [
        {
            field: 'type' as keyof AlarmsListItemDto,
            name: t('alarms.list.type') as string,
            orderColumn: 'st.type',
            width: 180,
            exportValue: row => t('alarms.types.' + row.type as any) as string,
            renderCell: row => <>{t('alarms.types.' + row.type as any)}</>,
        },
        {
            field: 'name' as keyof AlarmsListItemDto,
            name: t('alarms.list.name') as string,
            orderColumn: 'st.name',
            width: 250,
            renderCell: row => <>{row.name}</>,
        },
        {
            field: 'numberVehicles' as keyof AlarmsListItemDto,
            name: t('alarms.list.vehicles') as string,
            orderColumn: 'numberVehicles',
            width: 120,
            hideOn: ['sm', 'md', 'lg'],
            renderCell: row => <>{row.numberVehicles}</>,
        },
        {
            field: 'numberTriggers' as keyof AlarmsListItemDto,
            name: t('alarms.list.triggers') as string,
            orderColumn: 'numberTriggers',
            width: 120,
            hideOn: ['sm', 'md', 'lg'],
            renderCell: row => <>{row.numberTriggers}</>,
        },
        {
            field: 'updatedDate' as keyof AlarmsListItemDto,
            name: t('alarms.list.last_modification') as string,
            orderColumn: 'st.updated_date',
            width: 200,
            hideOn: ['sm', 'md'],
            exportValue: row => row.updatedDate ? moment(row.updatedDate).format(DATE_TIME_FORMAT_DEFAUT) : '',
            renderCell: row => row.updatedDate ? <DateFormat value={row.updatedDate} format={DATE_TIME_FORMAT_DEFAUT} /> : <>-</>,
        },
        {
            field: 'enabled' as keyof AlarmsListItemDto,
            name: t('alarms.list.active') as string,
            orderColumn: 'st.enabled',
            width: 100,
            hideOn: ['sm', 'md', 'lg', 'xl'],
            exportValue: row => t(`common.${row.enabled ? 'yes' : 'no'}` as any) as string,
            renderCell: row => (
                <label
                    className={`${toggleSwitchStyle.toggleSwitch} ${toggleSwitchStyle.enabled}`}>
                    <input
                        type='checkbox'
                        defaultChecked={row.enabled ? true : false}
                        checked={row.enabled}
                        onChange={() => null}
                    />
                    <span className={toggleSwitchStyle.switch} />
                </label>
            ),
        },
    ], [t]);

    return (
        <ScreenTitle title={t('alarms.title')}>
            <CompactPageLayout
                title={t('alarms.title') as string}
                contentAreaClassName={styles.contentArea}
                middleContent={
                    <div className={styles.searchContainer}>
                        <InputSearch
                            onChangeValue={debounced}
                            placeholder={t('alarms.list.search_by_name')}
                            defaultValue={criteria.allIn}
                        />
                    </div>
                }
                headerButtons={
                    <>
                        <ExportListingButton exportRef={exportListingRef} />
                        <ScreenHeaderButton icon={AddIcon} onClick={() => navigateTo('new')} />
                    </>
                }
            >
                <div className={compactPageLayoutStyles.listTblWrapper}>
                    {isFirstLoad.current === false && (
                        <DataTableWithSidebar
                            cacheKey="ALARMS_TABLE"
                            columns={tableColumns}
                            rows={alarms}
                            defaultOrder={order}
                            onOrderChange={handleOrderChange}
                            onRowClick={(row, index, event) => onRowClick(event, row, index)}
                            onHref={row => `alarms/details/${row.id}`}
                            onRenderAction={renderTableActionCell}
                            onRowId={row => row.id ?? ''}
                            scrollToRowId={scrollToItemId}
                            infiniteScroll={{
                                isLoading: isLoadingMore,
                                hasMore: getHasMore(alarms.length, totalItems),
                                onLoadMore: getOnLoadMore(loadMore),
                                hasMorePrevious: getHasMorePrevious(),
                                isLoadingPrevious,
                                onLoadPrevious: getOnLoadPrevious(loadPrevious),
                            }}
                            exportRef={exportListingRef}
                            hideExportInSidebar={true}
                            exportExcel={{
                                fetchAll: fetchAllForExport,
                                fileName: t('alarms.title') as string,
                            }}
                        />
                    )}
                </div>
                <QuestionYesNo message={t('common.messages.remove_record')}
                    isVisible={dialogDeleteItemIsOpen}
                    onYes={() => removeItem()}
                    onNo={() => setDialogDeleteItemIsOpen(false)} />
            </CompactPageLayout>
        </ScreenTitle>
    );
};

export default AlarmsListScreen;
